import { FIELD_GRID_CLASS, GridCells, smallInfoLabel, Row } from 'components/GridItemList';
import { PlusIcon } from 'components/icons';
import PartyName from 'components/party/PartyName';
import { useOpenSection } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import EditableInvoiceClientReference from 'pages/invoices/layout/views/components/EditableInvoiceClientReference';
import { useDetailsCardFields } from 'pages/invoices/layout/views/general-tab/DetailsCard';
import {
    InvoicePayersField,
    PayersForm,
    PayersInfoTooltip,
    useSaveInvoicePayers,
} from 'pages/invoices/payers/InvoicePayersField';
import { initialShareRateForNewPayer, shareAmountFromRate } from 'pages/invoices/payers/paymentShare';
import { ReactNode } from 'react';
import { Path, useFormContext, useFormState, UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { FeatureFlag } from 'types/features';
import { InvoiceForm } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useCanEditBillingNotes, useOpenBillingNotesEdit } from '../invoiceSections';
import { InvoiceTabProps } from '../invoiceTabProps';
import { InvoiceFormSection, isAnyFieldDirty, NO_SAVE, VALUE_ACTION_CLASS } from './GeneralCard';
import { PartyFactCells, partyFactRows } from './partyFacts';

const PAYER_SECTION_ID = 'payer';
const PAYER_FIELDS: Path<InvoiceForm>[] = ['clientParty', 'receiverParty'];

type Props = Pick<
    InvoiceTabProps,
    | 'invoice'
    | 'classifiers'
    | 'isInViewMode'
    | 'submitInvoice'
    | 'submitTemplateUpdate'
    | 'isTemplateEditable'
    | 'updateInvoiceLimited'
    | 'existingPrepayments'
>;

function ValueAction({ labelId, icon, onClick }: { labelId: string; icon?: ReactNode; onClick: () => void }) {
    return (
        <Button variant="ghost" size="sm" className={VALUE_ACTION_CLASS} onClick={onClick}>
            {icon}
            <FormattedMessage id={labelId} />
        </Button>
    );
}

/**
 * The Payer card's Save: the list of payers first, when the invoice keeps one, then the invoice when its payer or
 * service recipient changed. That save carries the list's new main payer, not the one the form loaded, which it would
 * put back.
 */
export async function savePayerCard({
    invoiceForm,
    payers,
    receivesInvoiceSave,
    submitInvoice,
}: {
    invoiceForm: Pick<UseFormReturn<InvoiceForm>, 'getValues' | 'setValue'>;
    payers?: { methods: UseFormReturn<PayersForm>; save: () => Promise<void> };
    receivesInvoiceSave: boolean;
    submitInvoice: () => Promise<boolean>;
}) {
    if (payers) {
        await payers.save();
        const mainPayer = payers.methods.getValues('payers.0.party');
        const formPayer = invoiceForm.getValues('clientParty');
        if (receivesInvoiceSave && mainPayer && formPayer && mainPayer.id !== formPayer.id) {
            invoiceForm.setValue('clientParty', { ...formPayer, id: mainPayer.id, name: mainPayer.name });
        }
    }
    return receivesInvoiceSave ? submitInvoice() : true;
}

/**
 * The payer as a card of its own after Settings, with the client reference and the service recipient; with several
 * payers it is Payers, their table first. Its Save saves the list of payers first, when the invoice keeps one, then
 * the invoice's payer and service recipient as today.
 */
export function PayerSection(props: Props) {
    const { invoice, submitInvoice, existingPrepayments } = props;
    const fields = useDetailsCardFields({
        ...props,
        submitInvoice: NO_SAVE,
        submitTemplateUpdate: NO_SAVE,
        updateInvoiceLimited: NO_SAVE,
    });
    const { payersForm } = fields;
    const savePayers = useSaveInvoicePayers(invoice, payersForm);
    const { control, getValues, setValue } = useFormContext<InvoiceForm>();
    const { dirtyFields } = useFormState({ control });
    const openBillingNotesEdit = useOpenBillingNotesEdit();
    const openSection = useOpenSection();
    const isServiceRecipientEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_SERVICE_RECIPIENT_FIELD);
    const showsBillingNotes = useCanEditBillingNotes(invoice);
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const { clientParty, receiverParty, hasPayerList, hasSeveralPayers, canAddPayers } = fields;

    // A row added here is in the list as the card opens, so the card opens on the table.
    const addPayer = () => {
        const { methods, report } = payersForm;
        const rows = methods.getValues('payers');
        const rate = initialShareRateForNewPayer(rows.map((row) => row.shareRate));
        const net = invoiceTotals?.invoiceCurrency.invoice.totalWithoutVat ?? 0;
        methods.setValue('payers', [
            ...rows,
            { party: null, shareRate: rate, shareAmount: shareAmountFromRate(net, rate) },
        ]);
        report('payer-added');
        openSection?.(PAYER_SECTION_ID);
    };

    const nameActions = (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {showsBillingNotes ? (
                <ValueAction
                    labelId="invoices.general-tab.details.edit-payer-billing-notes"
                    onClick={openBillingNotesEdit}
                />
            ) : null}
            {hasPayerList && !hasSeveralPayers && canAddPayers ? (
                <ValueAction
                    labelId="invoices.payers.add-multiple"
                    icon={<PlusIcon data-icon="inline-start" className="size-3.5" />}
                    onClick={addPayer}
                />
            ) : null}
        </span>
    );

    const payerRows: Row[] = clientParty ? partyFactRows(clientParty, nameActions) : [];
    if (hasSeveralPayers && payerRows[0]) {
        payerRows[0] = { ...payerRows[0], label: t('invoices.payers.main-payer') };
    } else if (payerRows[0] && existingPrepayments?.some((prepayment) => prepayment.isConnected)) {
        // Classic's hint beside the payer, which a connected prepayment keeps from changing.
        payerRows[0] = {
            ...payerRows[0],
            label: smallInfoLabel('official-name', 'invoices.tooltips.cannot-change-payer'),
        };
    }
    // An invoice with no service recipient holds an empty party, with no id.
    const receiver = receiverParty?.id ? receiverParty : null;
    const isReceiverThePayer = !!receiver && receiver.id === clientParty?.id;
    const rows: Row[] = [
        ...payerRows,
        {
            label: t('invoices.general-tab.details.client-ref'),
            content: (
                <EditableInvoiceClientReference
                    invoiceId={invoice.id}
                    clientReference={invoice.clientReference}
                    isEditEnabled={invoice.isEditable}
                />
            ),
        },
        ...(isServiceRecipientEnabled
            ? [
                  {
                      label: t('invoices.general-tab.details.receiver'),
                      content: receiver ? <PartyName party={receiver} isLink showPartyNumber /> : '-',
                      button: isReceiverThePayer ? (
                          <span className="text-muted-foreground font-normal">
                              <FormattedMessage id="invoices.general-tab.details.same-as-payer" />
                          </span>
                      ) : undefined,
                  },
                  ...(receiver && !isReceiverThePayer ? partyFactRows(receiver) : []),
              ]
            : []),
    ];

    const save = () =>
        savePayerCard({
            invoiceForm: { getValues, setValue },
            payers: hasPayerList ? { methods: payersForm.methods, save: savePayers } : undefined,
            receivesInvoiceSave: isAnyFieldDirty(dirtyFields, PAYER_FIELDS),
            submitInvoice,
        });

    // The payer's notes open in their own dialog from here: the side column's card would close this one unsaved.
    const billingNotesModalAction = showsBillingNotes ? (
        <ValueAction
            labelId="invoices.general-tab.details.edit-payer-billing-notes"
            onClick={fields.openPayerBillingNotes}
        />
    ) : undefined;

    return (
        <InvoiceFormSection
            id={PAYER_SECTION_ID}
            titleId={hasSeveralPayers ? 'invoices.payers.title' : 'invoices.general-tab.details.payer'}
            title={
                hasSeveralPayers ? (
                    <span className="flex items-center gap-1.5">
                        <FormattedMessage id="invoices.payers.title" />
                        <PayersInfoTooltip />
                    </span>
                ) : undefined
            }
            invoice={invoice}
            canEdit={fields.isPayerEditable || (isServiceRecipientEnabled && fields.isReceiverEditable)}
            rows={hasSeveralPayers ? [] : rows}
            fieldNames={PAYER_FIELDS}
            save={save}
            onCancel={() => {
                payersForm.methods.reset();
                payersForm.report('saved-list-loaded');
            }}
            fields={
                <>
                    {hasSeveralPayers ? (
                        <div>{fields.payerField({ inCard: true })}</div>
                    ) : (
                        <div className="form-field min-w-0 @2xl:col-span-2">
                            {fields.payerField({ inCard: true, labelAction: billingNotesModalAction })}
                        </div>
                    )}
                    {fields.clientRefField}
                    <PartyFactCells party={clientParty} />
                    {isServiceRecipientEnabled ? (
                        <>
                            <div className="form-field min-w-0 @2xl:col-span-2">{fields.receiverPicker}</div>
                            <PartyFactCells party={receiver} />
                        </>
                    ) : null}
                </>
            }
        >
            {hasSeveralPayers ? (
                <>
                    <InvoicePayersField
                        invoice={invoice}
                        payersForm={payersForm}
                        readOnlyNoteId={fields.payersReadOnlyNoteId}
                        isSinglePayerReadOnly
                        canAddPayers={false}
                        inCard
                        isReadView
                    />

                    <dl className={cn(FIELD_GRID_CLASS, 'm-0')}>
                        <GridCells rows={rows} />
                    </dl>
                </>
            ) : null}
        </InvoiceFormSection>
    );
}
